import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {OwnershipSelectProps} from "./types";
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    Popper,
    Radio,
    RadioGroup,
    TextField
} from "@mui/material";
import Chip from "@mui/material/Chip";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {useGetResponsiblePersonsQuery, useGetWorkspaceGroupsQuery} from "../../creations/api/creationsApiSlice";
import {AuthResponse} from "../../auth/types";
import type {PrincipalKind} from "./principalKey";
import {ownershipPrincipalKey, parseOwnershipPrincipalKey} from "./principalKey";

const OwnershipSelect: React.FC<OwnershipSelectProps> = ({
                                                             ownershipsAttributes,
                                                             onDelete,
                                                             onDeleteAll,
                                                             onAdd,
                                                             disabled,
                                                             small
                                                         }) => {
    const {t} = useTranslation();
    const {data: responsiblePersons} = useGetResponsiblePersonsQuery();
    const {data: workspaceGroups} = useGetWorkspaceGroupsQuery();
    const [openAddAuthorizedPersonModal, setOpenAddAuthorizedPersonModal] = useState(false);
    const [principalKind, setPrincipalKind] = useState<PrincipalKind>('User');
    const [selectedUser, setSelectedUser] = useState<{ id: number; email: string } | null>(null);
    const [selectedGroup, setSelectedGroup] = useState<{ id: number; name: string } | null>(null);
    const [authorizedPersonRights, setAuthorizedPersonRights] = useState('write');
    const user = JSON.parse(localStorage.getItem('user') || "{}") as AuthResponse || {};

    const selectedValues = (ownershipsAttributes || [])
        .filter(el => !el._destroy)
        .map(el => ownershipPrincipalKey(el.principalType as PrincipalKind, el.principalId));

    const resetModal = () => {
        setPrincipalKind('User');
        setSelectedUser(null);
        setSelectedGroup(null);
        setAuthorizedPersonRights('write');
    };

    const principalLabel = (key: string) => {
        const {principalType, principalId} = parseOwnershipPrincipalKey(key);
        const exists = ownershipsAttributes.some(el =>
            !el._destroy && el.principalType === principalType && el.principalId === principalId
        );
        if (!exists) {
            return ' ';
        }
        if (principalType === 'Group') {
            return workspaceGroups?.find(g => g.id === principalId)?.name ?? `Grupa #${principalId}`;
        }
        return responsiblePersons?.find(u => u.id === principalId)?.email ?? ' ';
    };

    return (<Box sx={{display: 'flex', alignItems: 'baseline'}}>
            <Autocomplete
                size={small ? 'small' : 'medium'}
                disabled={!!disabled}
                PopperComponent={(props) => <Popper {...props} style={{display: 'none'}}/>}
                className={'AuthorizedPersons_chip-list'}
                sx={{width: '100%'}}
                multiple
                id="tags-outlined"
                freeSolo
                options={selectedValues}
                value={selectedValues}
                isOptionEqualToValue={() => true}
                getOptionLabel={(option) => option}
                filterSelectedOptions
                renderTags={(tagValue) => {
                    return tagValue.map(val => <Chip
                        sx={{marginRight: '10px'}}
                        disabled={disabled}
                        label={(
                            <React.Fragment>
                                <span>{principalLabel(val)} | </span>
                                <strong>{t(`creations.steps.fillInCreationParameters.accessLevel.${ownershipsAttributes.filter(el => {
                                    const {principalType, principalId} = parseOwnershipPrincipalKey(val);
                                    return !el._destroy && el.principalType === principalType && el.principalId === principalId;
                                })[0]?.accessLevel ?? 'write'}`)}</strong>
                            </React.Fragment>)}
                        onDelete={(_) => {
                            onDelete(val)
                        }}
                    />)
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        onClick={(e) => {
                            if (disabled) {
                                return
                            }
                            if (e.target instanceof SVGElement || e.target instanceof HTMLElement) {
                                const classList = Array.from(e.target.classList)
                                if (classList.length === 0 || classList.some(element => element.includes('fontSizeSmall') || classList.some(element => element.includes('clearIndicator')))) {
                                    onDeleteAll()
                                    return
                                }
                            }
                            resetModal();
                            setOpenAddAuthorizedPersonModal(true)
                        }
                        }
                        label="Dodaj uprawnienia"
                        placeholder="Użytkownik lub grupa"
                    />
                )}
            />
            <Dialog
                open={openAddAuthorizedPersonModal}
                onClose={() => {
                    setOpenAddAuthorizedPersonModal(false)
                    resetModal();
                }}
                fullWidth={true}
                maxWidth="md"
            >

                <DialogTitle id="alert-dialog-title">
                    Dodaj uprawnienia
                </DialogTitle>
                <DialogContent>
                    <FormControl component="fieldset" sx={{marginTop: 1, width: '100%'}}>
                        <FormLabel component="legend">Dla</FormLabel>
                        <RadioGroup
                            row
                            value={principalKind}
                            onChange={(e) => {
                                setPrincipalKind(e.target.value as PrincipalKind);
                                setSelectedUser(null);
                                setSelectedGroup(null);
                            }}
                        >
                            <FormControlLabel value="User" control={<Radio/>} label="Użytkownika"/>
                            <FormControlLabel value="Group" control={<Radio/>} label="Grupy"/>
                        </RadioGroup>
                    </FormControl>
                    <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1}}>
                        {principalKind === 'User' && responsiblePersons && (
                            <Autocomplete
                                noOptionsText={'Brak danych...'}
                                getOptionLabel={(value) => value.email || ''}
                                value={selectedUser}
                                onChange={(_, value) => setSelectedUser(value)}
                                options={responsiblePersons.filter(el =>
                                    el.email !== user.profile?.email &&
                                    !(ownershipsAttributes || []).some(selected =>
                                        selected.principalType === 'User' &&
                                        selected.principalId === el.id &&
                                        !selected._destroy
                                    )
                                )}
                                sx={{width: 300, marginTop: '10px'}}
                                renderInput={(params) => <TextField {...params} label="Użytkownik"/>}
                            />)}
                        {principalKind === 'Group' && workspaceGroups && (
                            <Autocomplete
                                noOptionsText={'Brak grup...'}
                                getOptionLabel={(value) => value.name || ''}
                                value={selectedGroup}
                                onChange={(_, value) => setSelectedGroup(value)}
                                options={workspaceGroups.filter(g =>
                                    !(ownershipsAttributes || []).some(selected =>
                                        selected.principalType === 'Group' &&
                                        selected.principalId === g.id &&
                                        !selected._destroy
                                    )
                                )}
                                sx={{width: 300, marginTop: '10px'}}
                                renderInput={(params) => <TextField {...params} label="Grupa"/>}
                            />)}
                        <FormControl sx={{marginLeft: '10px', marginTop: '10px'}}>
                            <InputLabel id="demo-simple-select-label">Uprawnienia</InputLabel>
                            <Select
                                sx={{width: '150px'}}
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                value={authorizedPersonRights}
                                label="Uprawnienia"
                                onChange={(e) => {
                                    setAuthorizedPersonRights(e.target.value as string)
                                }}
                            >
                                <MenuItem value={'write'}>Edycja</MenuItem>
                                <MenuItem value={'read'}>Wyświetlanie</MenuItem>
                            </Select>
                        </FormControl>

                    </Box>

                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setOpenAddAuthorizedPersonModal(false)
                            resetModal();
                        }}>{t('common.closeWindow')}</Button>
                    <Button
                        disabled={principalKind === 'User' ? !selectedUser : !selectedGroup}
                        variant={'contained'} onClick={() => {
                        const id = principalKind === 'User' ? selectedUser!.id : selectedGroup!.id;
                        setOpenAddAuthorizedPersonModal(false)
                        onAdd(id, authorizedPersonRights, principalKind)
                        resetModal();

                    }}>Dodaj</Button>
                </DialogActions>
            </Dialog>
        </Box>

    )
}

export default OwnershipSelect;
